import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  discoverMicrosoftTenants,
  getAuthStatus,
  getAvailableTenants,
  loginMicrosoftTenant,
  loginServicePrincipal,
  logout
} from "../lib/api";
import { acquireMicrosoftTenantToken, signInWithMicrosoft } from "../lib/microsoftAuth";
import { isAzureDataQueryKey } from "../lib/queryKeys";
import type { ServicePrincipalCredentials } from "../lib/types";

export function useAuth() {
  const queryClient = useQueryClient();
  const resetAzureData = async () => {
    const predicate = (query: { queryKey: readonly unknown[] }) => isAzureDataQueryKey(query.queryKey);
    queryClient.removeQueries({ predicate, type: "inactive" });
    await queryClient.resetQueries({ predicate, type: "active" });
  };
  const session = useQuery({
    queryKey: ["auth"],
    queryFn: getAuthStatus,
    staleTime: 60_000,
    retry: false
  });

  const login = useMutation({
    mutationFn: (credentials: ServicePrincipalCredentials) => loginServicePrincipal(credentials),
    onSuccess: async (data) => {
      queryClient.setQueryData(["auth"], data);
      await resetAzureData();
    }
  });

  const discoverMicrosoft = useMutation({
    mutationFn: async () => {
      const result = await signInWithMicrosoft();
      return discoverMicrosoftTenants(result.accessToken);
    }
  });

  const connectMicrosoftTenant = useMutation({
    mutationFn: async (tenantId: string) => {
      const result = await acquireMicrosoftTenantToken(tenantId);
      return loginMicrosoftTenant(result.accessToken, tenantId);
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(["auth"], data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["tenants"] }),
        resetAzureData()
      ]);
    }
  });

  const tenants = useQuery({
    queryKey: ["tenants"],
    queryFn: getAvailableTenants,
    enabled: session.data?.authenticated === true && session.data.authType === "microsoft",
    staleTime: 5 * 60_000,
    retry: false
  });

  const signOut = useMutation({
    mutationFn: logout,
    onSuccess: (data) => {
      queryClient.setQueryData(["auth"], data);
      queryClient.removeQueries({
        predicate: (query) => isAzureDataQueryKey(query.queryKey)
      });
    }
  });

  return {
    session,
    login,
    discoverMicrosoft,
    connectMicrosoftTenant,
    tenants,
    signOut
  };
}
